/**
 * ParticleGLLayer.ts
 * محرّك جسيمات الرياح — إعادة بناء كاملة بأسلوب Zoom Earth، كـ MapLibre Custom Layer.
 *
 * ── لماذا أُعيد البناء من الصفر ───────────────────────────────────────────────
 * النسخة السابقة كانت مبنية على webgl-wind (محاكاة في فضاء الشبكة الجغرافية). ذلك
 * الأسلوب يجرّ معه ثلاث مشاكل بنيوية لا تُحلّ بالمعايرة:
 *   1. السرعة البصرية تتغيّر مع الزووم، فتُعالَج بمعامل أُسّي تقريبي.
 *   2. دقّة تخزين الموضع تتناقص مع الزووم (الموضع نسبةً إلى الكرة) فتتجمّد الجسيمات.
 *   3. لا طريقة صحيحة لمتابعة الذيول للأرض أثناء السحب/التكبير، فكانت تُمسَح عند كل
 *      حركة ثم يُعاد بذر كل شيء عند انتهاء الزووم → وميض وقفزات.
 *
 * ── النموذج الجديد (وهو نموذج Zoom Earth نفسه) ────────────────────────────────
 * المحاكاة تجري **في فضاء الشاشة**:
 *   • كل جسيمة نقطة داخل نافذة العرض، تتحرّك عدداً ثابتاً من البكسلات لكل (م/ث) من
 *     سرعة الرياح: px += u · 0.075 / max(1, 5 − zoom) لكل إطار — أرقام Zoom Earth
 *     الحرفية. السرعة البصرية ثابتة في كل الزوومات، والعرض العالمي أبطأ تدريجياً.
 *   • عند تغيّر العرض (سحب/تكبير/تدوير/إمالة) نحسب تحويلاً إسقاطياً واحداً من شاشة
 *     الإطار السابق إلى شاشة الإطار الحالي، ونطبّقه على **مواضع الجسيمات وعلى نسيج
 *     الذيول معاً**. النتيجة: كل شيء يبقى ملتصقاً بالأرض أثناء الحركة — لا مسح، لا
 *     إعادة بذر، لا وميض عند بدء/انتهاء التكبير.
 *   • الذيل يُبنى من تراكم قطع الإطارات في نسيج مع تلاشٍ أُسّي (تماماً كـ Zoom Earth)،
 *     وقطعة كل إطار تُرسم من الموضع **المخزَّن** في الإطار السابق لا من تقدير عكسي،
 *     فتلتحم القطع بلا فجوات حتى في الدوران الحادّ قرب مركز الإعصار.
 *   • لكل جسيمة عمر محدود؛ تظهر بتلاشٍ قصير وتختفي بتلاشٍ أطول قبل موتها ثم تُولَد من
 *     جديد في موضع عشوائي — فلا وميض ولا اختفاء مفاجئ.
 *
 * الحالة كلها في نسيجَي RGBA8 (لا إضافات float، ولا فروق بين الأجهزة) — التخزين نسبةً
 * إلى نافذة العرض يعطي دقّة ≈ 0.02 بكسل في كل مستويات الزووم.
 */

import { type CustomLayerInterface, type CustomRenderMethodInput, type Map as MaplibreMap } from 'maplibre-gl';
import type { WeatherGrid } from '../../../services/weatherGridService';
import { type GLContext, createProgram, getLocations, createTexture, createBuffer } from './glUtils';
import { buildValueTexture, WIND_UV_MAX } from './weatherTextures';
import { STATE_VERT, UPDATE_FRAG, SCREEN_VERT, SCREEN_FRAG, DRAW_VERT, DRAW_FRAG } from './particleShaders';
import { type Mat3, IDENTITY3, squareToQuad, invert3, multiply3, toColumnMajor, isFinite3 } from './homography';

// ── ثوابت Zoom Earth ─────────────────────────────────────────────────────────

/**
 * بكسل CSS لكل (متر/ثانية) من سرعة الرياح في كل إطار عند 60fps.
 *
 * الرقم الحرفي في حزمة Zoom Earth هو 0.075 (px += u · 0.075)، لكنه يفترض وحدة مُدخَل
 * لم نتمكّن من التأكّد منها (m/s مقابل km/h). القيمة 0.075 مع u بالمتر/الثانية تعطي حركة
 * أبطأ بوضوح ممّا تُظهره Zoom Earth/Windy فعلياً، فعوّلنا على القياس البصري: 0.15 —
 * رياح 10 م/ث ⇒ 1.5 بكسل/إطار ⇒ 90 بكسل/ثانية. يبقى مضاعف السرعة في الإعدادات
 * (settings.speed) هو المقبض النهائي.
 */
const ZE_PX_PER_MS = 0.075;

/**
 * إبطاء العرض العالمي: ZE تقسم على max(1, 5 − zoom)، لكن zoom عندها **مستوى OpenLayers**
 * (بلاطات 256 بكسل: ‏(Nm − ln res)·log2e في مصدرها Rm)، بينما zoom في MapLibre بلاطات 512
 * — أي أقلّ بواحد للمنظر نفسه. أخذ الرقم 5 حرفياً مع zoom الخاصّ بـ MapLibre كان يُبطئ
 * كل مستوى ≤ 4 ضعف ZE (والذيل يقصر بالنسبة نفسها): خيوط قصيرة باهتة في العرض البعيد.
 * بالمكافئ: max(1, 4 − zoomMapLibre).
 */
const ZE_SLOW_ZOOM = 4;

/**
 * كثافة Zoom Earth الحرفية (قُرئت من حزمتها app.24434048.js في 2026-09-26):
 *   count = clamp(4·round(W·H·0.0025/4), 500, 8000) — W وH بكسل CSS (مصدر Rm يحسبهما
 *   extent/resolution، لا بكسلاً فيزيائياً). أي جسيمة لكل 400 بكسل²، ثابتة في كل زووم.
 */
const DENSITY_PER_CSS_PX = 0.0025;
const ZE_MIN_PARTICLES = 500;
const ZE_MAX_PARTICLES = 8000;
/** سماكات ZE: أربع مجموعات 2.25·(k+1)/4 = 0.56..2.25 بكسل CSS، متوسّطها 1.41. */
const ZE_WIDTH_MEAN = 1.40625;
const ZE_WIDTH_VAR = 1.2;
/*
 * بقيّة أرقام ZE الحرفية. **مثبّتة في الكود عمداً ولا تقرأ الإعدادات** (لوحة الأدمن/المستخدم):
 * قيم الأدمن المحفوظة (سرعة 1.4، ذيل 0.45، عمر 0.8…) كانت تُبعد الشكل عن ZE بصمت.
 *   الذيل: fillRect أسود بشفافية 0.08 كل إطار ⇒ إبقاء 0.92.
 *   العمر: age يبدأ عشوائياً 0..200 ويزيد بخطوة الإطار، وتُعاد الجسيمة عند 200.
 *   اللون #c3ddfa، وشفافية الطبقة 2/3 (الوضع الداكن).
 */
const ZE_TRAIL_KEEP = 0.92;
const ZE_MAX_AGE = 200;
const ZE_OPACITY = 2 / 3;
const ZE_COLOR = '#c3ddfa';

/**
 * أدنى طول للشرطة المرسومة كل إطار (بكسل CSS). عند 16 كم/س تكون إزاحة الإطار ~0.33
 * بكسل فقط، فبدون هذا الحدّ تُرسم الجسيمة نقطةً باهتة بدل خيط واضح.
 */
const MIN_SEGMENT_CSS_PX = 0.8;   // ZE بلا حدّ أدنى؛ 0.8 تُبقي الهواء الساكن نقاطاً صغيرة كما عندها

/** هامش تخزين خارج النافذة: يسمح للجسيمات بالخروج قليلاً والعودة بدل الموت على الحافة. */
const VIEW_MARGIN = 0.08;

/** دقّة نسيج الحالة (128² = 16384 جسيمة كحدّ أقصى — أكثر ممّا يُرسم فعلياً بكثير). */
const STATE_RES = 128;

/** أقصى قيمة رياح مرمَّزة في النسيج، بالمتر/الثانية (WIND_UV_MAX بالكيلومتر/الساعة). */
const UV_MAX_MS = WIND_UV_MAX / 3.6;

// ── الإعدادات ────────────────────────────────────────────────────────────────

export interface ParticleSettings {
    speed: number;              // مضاعف سرعة (1 = سرعة Zoom Earth الحرفية)
    trail: number;              // 0..1 طول الذيل
    opacity: number;            // 0..1 سطوع الجسيمات
    density: number;            // 0..1+ كثافة (تُضرب في عدد الجسيمات لكل مساحة شاشة)
    lineWidth: number;          // 0..1 متوسط سمك الخط
    lineWidthVariation: number; // 0..1 نثر سمك الخط
    lifetime: number;           // 0..1 عمر الجسيمة (عابر↔مستمر)
    color: string;              // لون الخطوط (hex)
}

/** يحوّل "#rrggbb" إلى [r,g,b] بمدى 0..1. يتجاهل صيغاً غير صالحة (يبقى أبيض). */
function hexToRgb01(hex: string): [number, number, number] {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex);
    if (!m) return [1, 1, 1];
    const n = parseInt(m[1], 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

/** خط طول/عرض → مركاتور الشبكي [0,1] (y = 0 عند الشمال). */
function lngLatToMercator(lng: number, lat: number): [number, number] {
    const x = (lng + 180) / 360;
    const clamped = Math.max(-89.9999, Math.min(89.9999, lat));
    const s = Math.sin((clamped * Math.PI) / 180);
    const y = 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
    return [x, y];
}

export class ParticleGLLayer implements CustomLayerInterface {
    public readonly id: string;
    public readonly type = 'custom' as const;
    public readonly renderingMode = '2d' as const;

    private map: MaplibreMap | null = null;
    private gl: GLContext | null = null;
    private removed = false;

    /**
     * مقاس الحاوية بالبكسل المنطقي — **مخزَّن، لا يُقرأ من DOM في حلقة الرسم**.
     *
     * `container.clientWidth/clientHeight` قراءةٌ تُجبر المتصفّح على إعادة حساب التخطيط
     * (‏forced synchronous layout) قبل أن يُرجع الرقم. كانت تُقرأ **ثلاث مرّات في كل نبضة
     * رسم للجسيمات** (‏computeScreenToMercator مرّتين وrenderInner مرّة)، وظهر أثرها في
     * trace الخيط الرئيسي كـ**1.88 ثانية (6.6%)** تحت `get clientWidth` — أي ثاني أغلى
     * بند بعد بناء تراكم الأمطار، وكلّه عملٌ لا يُنتج بكسلاً واحداً.
     *
     * المقاس لا يتغيّر إلا عند تغيّر حجم النافذة، وMapLibre يُطلق `resize` عندها — فقراءة
     * واحدة لكل تغيير تكفي بدل ستّين في الثانية.
     */
    private cssW = 0;
    private cssH = 0;
    private onMapResize = () => { this.readContainerSize(); };

    private readContainerSize(): void {
        const el = this.map?.getContainer();
        if (!el) return;
        this.cssW = el.clientWidth;
        this.cssH = el.clientHeight;
    }

    // برامج الشيدر
    private updateProgram: WebGLProgram | null = null;
    private drawProgram: WebGLProgram | null = null;
    private screenProgram: WebGLProgram | null = null;
    private updateLoc: ReturnType<typeof getLocations> | null = null;
    private drawLoc: ReturnType<typeof getLocations> | null = null;
    private screenLoc: ReturnType<typeof getLocations> | null = null;

    // مخازن ونسج
    private quadBuffer: WebGLBuffer | null = null;
    private indexBuffer: WebGLBuffer | null = null;
    private cornerBuffer: WebGLBuffer | null = null;
    private framebuffer: WebGLFramebuffer | null = null;
    // فتحتا نسيج الرياح: الساعة الأدنى والأعلى المحيطتان بالزمن المعروض. كل فتحة تتذكّر
    // أي ساعة تحملها، فعند تقدّم الساعة تُبدَّل الفتحتان بدل إعادة رفع نسيج كامل.
    private windSlots: Array<{ tex: WebGLTexture | null; idx: number; grid: WeatherGrid | null }> = [
        { tex: null, idx: -9999, grid: null },
        { tex: null, idx: -9999, grid: null },
    ];
    private posTex0: WebGLTexture | null = null;
    private posTex1: WebGLTexture | null = null;
    private ageTex0: WebGLTexture | null = null;
    private ageTex1: WebGLTexture | null = null;
    private screenTex0: WebGLTexture | null = null;
    private screenTex1: WebGLTexture | null = null;
    private screenW = 0;
    private screenH = 0;

    // الحالة
    // شبكات الساعات الثلاث المحيطة بالزمن الحالي (index−1، index، index+1) ومؤشّر index.
    private gridPrev: WeatherGrid | null = null;
    private gridCur: WeatherGrid | null = null;
    private gridNext: WeatherGrid | null = null;
    private gridIndex = 0;

    // الزمن المعروض بالساعات (index + fraction) — متّصل، يلاحق الهدف المتقطّع بتدرّج.
    private shownTime = 0;
    private targetTime = Number.NaN;
    private rampFrom = 0;
    private rampTo = 0;
    private rampStart = 0;
    private rampMs = 0;
    private lastTargetMs = 0;
    private numParticles = STATE_RES * STATE_RES;
    private darkBase = true;
    private numDrawn = 0;
    private smoothCount = 0;   // عدد الخيوط منعَّماً زمنياً (يمنع الانبثاق عند قفزات الزووم)
    /**
     * بذر كامل (توزيع جديد بأعمار مبعثرة + مسح الذيول) — أول إطار فقط.
     * لا شيء في التفاعل العادي يعيد البذر: التكبير/التصغير/السحب تتابعها الجسيمات عبر
     * التحويل الإسقاطي، وسحب الشريط الزمني تتابعه بانسيابها في حقل الرياح الجديد — كما
     * Zoom Earth. أي إعادة بذر أثناء التفاعل تظهر كوميض أو انقطاع.
     */
    private pendingReseed = true;
    private clearTrails = true;
    private lastFrameMs = 0;
    private frameStep = 1;
    private prevS2m: Mat3 | null = null;   // شاشة الإطار السابق → مركاتور
    private prevZoom: number | null = null;
    private errorLogged = false;

    // مصفوفات مؤقتة (لا تخصيص داخل حلقة الرسم)
    private readonly tmpMat = new Float32Array(9);
    private readonly tmpMat2 = new Float32Array(9);

    // الافتراضيات = NATURAL_PARTICLE_PRESET؛ الإعدادات الحقيقية تصل عبر props.
    private settings: ParticleSettings = {
        speed: 1.0,
        trail: 0.55,
        opacity: 0.95,
        density: 1.0,
        lineWidth: 0.5,
        lineWidthVariation: 0.35,
        lifetime: 0.6,
        color: '#f4fbff',
    };

    constructor(id: string, settings?: Partial<ParticleSettings>, darkMode = true) {
        this.darkBase = darkMode;
        this.id = id;
        if (settings) this.settings = { ...this.settings, ...settings };
    }

    // ── واجهة عامة ────────────────────────────────────────────────────────────

    /** شبكات الساعات المحيطة بالزمن الحالي. index = ساعة `cur`. */
    setWindGrids(prev: WeatherGrid | null, cur: WeatherGrid | null, next: WeatherGrid | null, index: number) {
        this.gridPrev = prev;
        this.gridCur = cur;
        this.gridNext = next;
        this.gridIndex = index;
        this.map?.triggerRepaint();
    }

    /**
     * الزمن المطلوب من الشريط الزمني. المتجر يتقدّم بخطوات 1/6 ساعة متقطّعة (سواء في
     * التشغيل التلقائي أو أثناء السحب)، ولو تبِعها حقل الرياح حرفياً لانكسرت كل الخيوط
     * دفعةً واحدة ستّ مرّات في الساعة — وهو ما يراه المستخدم كـ"انكسارات".
     *
     * لذلك نقيس الفاصل الزمني بين خطوتين متتاليتين ونتدرّج نحو الهدف على مدى الفاصل
     * نفسه، فيصل الزمن المعروض إلى الهدف تماماً لحظة وصول الخطوة التالية: الحقل يتقدّم
     * متّصلاً بلا قفزة واحدة. القفزات الكبيرة (نقر تاريخ بعيد) تُطبَّق فوراً بلا تدرّج.
     */
    setModelTime(index: number, fraction: number) {
        const t = index + fraction;
        if (t === this.targetTime) return;
        const now = typeof performance === 'undefined' ? 0 : performance.now();
        const gap = this.lastTargetMs > 0 ? now - this.lastTargetMs : 0;
        this.lastTargetMs = now;

        if (!Number.isFinite(this.targetTime) || Math.abs(t - this.shownTime) > 1.5) {
            this.shownTime = t;
            this.rampMs = 0;
        } else {
            this.rampFrom = this.shownTime;
            this.rampTo = t;
            this.rampStart = now;
            this.rampMs = Math.max(60, Math.min(1500, gap > 0 ? gap : 220));
        }
        this.targetTime = t;
        this.map?.triggerRepaint();
    }

    /**
     * مظهر الجسيمات مستقلّ عن سمة الخريطة: لونها يأتي من settings.color وحدها (خيط
     * Zoom Earth الأبيض نفسه فوق الحقل اللوني في السمتين). تبقى الدالة موجودة لتوافق
     * الغلاف، وتكتفي بطلب إعادة رسم.
     */
    setDarkMode(darkMode: boolean) {
        this.darkBase = darkMode;
        this.map?.triggerRepaint();
    }

    setSettings(settings: Partial<ParticleSettings>) {
        this.settings = { ...this.settings, ...settings };
        this.map?.triggerRepaint();
    }

    // ── دورة الحياة ───────────────────────────────────────────────────────────

    onAdd(map: MaplibreMap, gl: GLContext) {
        this.map = map;
        this.gl = gl;
        this.removed = false;
        this.readContainerSize();
        map.on('resize', this.onMapResize);

        try {
            this.updateProgram = createProgram(gl, STATE_VERT, UPDATE_FRAG);
            this.drawProgram = createProgram(gl, DRAW_VERT, DRAW_FRAG);
            this.screenProgram = createProgram(gl, SCREEN_VERT, SCREEN_FRAG);

            this.updateLoc = getLocations(gl, this.updateProgram, ['a_pos'], [
                'u_pos', 'u_age', 'u_wind', 'u_wind_next', 'u_wind_blend',
                'u_prev_to_cur', 'u_s2m', 'u_bounds', 'u_wrap_x',
                'u_step', 'u_frame_step', 'u_age_step', 'u_margin', 'u_rand_seed', 'u_reseed',
                'u_out_age', 'u_uv_max', 'u_scale_kill',
            ]);
            this.drawLoc = getLocations(gl, this.drawProgram, ['a_index', 'a_corner'], [
                'u_pos_new', 'u_pos_old', 'u_age_new', 'u_prev_to_cur', 'u_res', 'u_resolution',
                'u_margin', 'u_width', 'u_width_var', 'u_alpha', 'u_max_seg', 'u_min_seg', 'u_color',
            ]);
            this.screenLoc = getLocations(gl, this.screenProgram, ['a_pos'],
                ['u_screen', 'u_opacity', 'u_floor', 'u_warp']);

            this.quadBuffer = createBuffer(gl, new Float32Array([0, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 1]));
            this.framebuffer = gl.createFramebuffer();
            this.windSlots[0].tex = this.createWindTexture(gl);
            this.windSlots[1].tex = this.createWindTexture(gl);

            this.initParticles(gl);
            this.initGeometryBuffers(gl);
        } catch (e) {
            // فشل توقيتي محتمل أثناء إضافة عدّة طبقات دفعة واحدة — نعيد المحاولة في الإطار
            // التالي بدل ترك الطبقة معطوبة صامتة.
            console.error(`ParticleGLLayer(${this.id}) onAdd failed, retrying:`, e);
            requestAnimationFrame(() => {
                if (this.removed || !this.map || !this.gl) return;
                this.onAdd(this.map, this.gl);
            });
        }
    }

    onRemove(_map: MaplibreMap, gl: GLContext) {
        this.removed = true;
        this.map?.off('resize', this.onMapResize);
        // قبل إتلاف المخازن: عطّل خاصيات الرأس المفعّلة، وإلا بقيت تشير إلى مخازن محذوفة
        // فيفشل رسم بقية طبقات WebGL المخصّصة بـ INVALID_OPERATION.
        this.disableOwnAttribs(gl);
        for (const p of [this.updateProgram, this.drawProgram, this.screenProgram]) if (p) gl.deleteProgram(p);
        for (const b of [this.quadBuffer, this.indexBuffer, this.cornerBuffer]) if (b) gl.deleteBuffer(b);
        if (this.framebuffer) gl.deleteFramebuffer(this.framebuffer);
        for (const t of [this.windSlots[0].tex, this.windSlots[1].tex,
            this.posTex0, this.posTex1, this.ageTex0, this.ageTex1,
            this.screenTex0, this.screenTex1]) {
            if (t) gl.deleteTexture(t);
        }
        this.map = this.gl = null;
    }

    // ── تهيئة الموارد ─────────────────────────────────────────────────────────

    /** نسيج الرياح: تكرار أفقي (الشبكة عالمية فيلتفّ الطول بلا درز عند ±180). */
    private createWindTexture(gl: GLContext): WebGLTexture {
        const tex = createTexture(gl, 1, 1, new Uint8Array([128, 128, 128, 0]), { filter: gl.LINEAR });
        const canRepeat = typeof WebGL2RenderingContext !== 'undefined' && gl instanceof WebGL2RenderingContext;
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, canRepeat ? gl.REPEAT : gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        return tex;
    }

    private initParticles(gl: GLContext) {
        for (const t of [this.posTex0, this.posTex1, this.ageTex0, this.ageTex1]) if (t) gl.deleteTexture(t);
        const n = STATE_RES * STATE_RES;
        const pos = new Uint8Array(n * 4);
        const age = new Uint8Array(n * 4);
        for (let i = 0; i < n; i++) {
            // مواضع عشوائية بدقّة 16-bit، وأعمار مبعثرة كي لا تموت الجسيمات كلها معاً.
            const x = Math.round(Math.random() * 65535);
            const y = Math.round(Math.random() * 65535);
            pos[i * 4 + 0] = x >> 8; pos[i * 4 + 1] = x & 255;
            pos[i * 4 + 2] = y >> 8; pos[i * 4 + 3] = y & 255;
            const a = Math.round(Math.random() * 65535);
            age[i * 4 + 0] = a >> 8; age[i * 4 + 1] = a & 255;
            age[i * 4 + 2] = Math.floor(Math.random() * 256);
            age[i * 4 + 3] = 255;   // "وُلدت للتوّ" → لا ذيل في أول إطار
        }
        const opts = { filter: gl.NEAREST };
        this.posTex0 = createTexture(gl, STATE_RES, STATE_RES, pos, opts);
        this.posTex1 = createTexture(gl, STATE_RES, STATE_RES, new Uint8Array(pos), opts);
        this.ageTex0 = createTexture(gl, STATE_RES, STATE_RES, age, opts);
        this.ageTex1 = createTexture(gl, STATE_RES, STATE_RES, new Uint8Array(age), opts);
    }

    // كل جسيمة رباعي (٦ رؤوس/مثلثان): 0/1 = طرف الرأس (يسار/يمين)، 2/3 = طرف الذيل.
    private static readonly VERTS_PER_PARTICLE = 6;
    private static readonly CORNER_PATTERN = [0, 1, 2, 1, 3, 2];

    private initGeometryBuffers(gl: GLContext) {
        if (this.indexBuffer) gl.deleteBuffer(this.indexBuffer);
        if (this.cornerBuffer) gl.deleteBuffer(this.cornerBuffer);
        const vpp = ParticleGLLayer.VERTS_PER_PARTICLE;
        const pattern = ParticleGLLayer.CORNER_PATTERN;
        const indices = new Float32Array(this.numParticles * vpp);
        const corners = new Float32Array(this.numParticles * vpp);
        for (let i = 0; i < this.numParticles; i++) {
            for (let v = 0; v < vpp; v++) {
                indices[i * vpp + v] = i;
                corners[i * vpp + v] = pattern[v];
            }
        }
        this.indexBuffer = createBuffer(gl, indices);
        this.cornerBuffer = createBuffer(gl, corners);
    }

    /** شبكة الساعة k إن كانت متاحة ضمن الثلاث الممرَّرة. */
    private gridForIndex(k: number): WeatherGrid | null {
        if (k === this.gridIndex) return this.gridCur;
        if (k === this.gridIndex - 1) return this.gridPrev;
        if (k === this.gridIndex + 1) return this.gridNext;
        return null;
    }

    /**
     * يضمن أن الفتحة `slot` تحمل شبكة الساعة `idx`. إن كانت الفتحة الأخرى تحملها أصلاً
     * (الحالة المعتادة عند تقدّم الساعة) نبدّل الفتحتين بلا أي رفع جديد.
     */
    private ensureWindSlot(gl: GLContext, slot: number, idx: number, grid: WeatherGrid | null) {
        const s = this.windSlots[slot];
        if (s.idx === idx && s.grid === grid) return;
        const other = this.windSlots[1 - slot];
        if (other.idx === idx && other.grid === grid) {
            this.windSlots[slot] = other;
            this.windSlots[1 - slot] = s;
            return;
        }
        if (!grid || !s.tex) return;
        const { data, width, height } = buildValueTexture(grid);
        gl.bindTexture(gl.TEXTURE_2D, s.tex);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
        s.idx = idx;
        s.grid = grid;
    }

    private ensureScreenTextures(gl: GLContext) {
        const w = gl.drawingBufferWidth;
        const h = gl.drawingBufferHeight;
        if (w === this.screenW && h === this.screenH && this.screenTex0) return;
        this.screenW = w;
        this.screenH = h;
        if (this.screenTex0) gl.deleteTexture(this.screenTex0);
        if (this.screenTex1) gl.deleteTexture(this.screenTex1);
        const empty = new Uint8Array(w * h * 4);
        // LINEAR: نسيج الذيول يُعاد إسقاطه بكسور بكسل كل إطار أثناء الحركة، ومع NEAREST
        // يُقرَّب للشبكة فتتكسّر الخيوط إلى نقاط متقطعة.
        this.screenTex0 = createTexture(gl, w, h, empty, { filter: gl.LINEAR });
        this.screenTex1 = createTexture(gl, w, h, new Uint8Array(empty), { filter: gl.LINEAR });
    }

    // ── حساب هندسة العرض ──────────────────────────────────────────────────────

    /**
     * التحويل الإسقاطي: نافذة العرض بنسبة [0,1]² (0,0 = أعلى-يسار) → مركاتور [0,1]².
     * يُبنى من أركان النافذة الأربعة، فيصحّ مع السحب والتكبير والتدوير والإمالة معاً.
     * إحداثي x قد يخرج من [0,1] عند عبور خط التاريخ أو عرض نسخ العالم — وهو مقصود
     * (الاستمرارية)، ويُلتفّ لاحقاً عند أخذ عيّنة الرياح فقط.
     *
     * ── فكّ الالتفاف: خطوات من مركز الشاشة، لا قفزة واحدة إلى الركن ───────────
     *
     * كان كل ركن يُفكّ التفافه مستقلّاً عن المركز بـ`x -= round(x - centerX)`. وذلك
     * صحيح ما دام الركن أقرب من نصف عالم إلى المركز، **وينهار عند بلوغ المنظر 360°
     * عرضاً**: الحافّة اليسرى تبعد −0.5 والحافّة اليمنى +0.5 بالضبط، و`Math.round`
     * يُقرّب النصف الموجب إلى 1 والسالب إلى صفر — فتُطرح دورة كاملة من الركن الأيمن
     * وحده، فينطبق على الأيسر ويصير الضلع العلوي طوله صفر. والمصفوفة المبنيّة على
     * رباعيٍّ منهار تبقى منتهية عددياً (فلا يمسكها `isFinite3`) لكنها تُسقط كل عرض
     * الشاشة على خطّ طولٍ واحد تقريباً: فتقرأ كل الجسيمات المتجاورة أفقياً نفس متجه
     * الرياح وتسير معاً في خطوط مستقيمة متوازية — وهو العَرَض المرصود بالحرف: خطوط
     * أفقية غير منطقية عند التصغير على سطح المكتب، وسلامة تامّة على الجوّال (نافذته
     * الضيّقة لا تبلغ 360° عند تلك المستويات) وعند التقريب (المدى دون نصف عالم).
     *
     * والعلاج أن يُفكّ الالتفاف **تراكمياً**: نسير من مركز الشاشة — وهو المرجع الوحيد
     * المعروف يقيناً (‏x = centerX) — إلى الركن في خطوات، ويُفكّ التفاف كل خطوة قياساً
     * إلى سابقتها. فما دامت الخطوة الواحدة أقصر من نصف عالم بقي `round` بلا التباس،
     * مهما اتّسع المنظر. وعدد الخطوات يُشتقّ من اتّساع المنظر نفسه، فيبقى واحداً
     * (وبكلفة اليوم بالضبط) في كل منظر أضيق من عالم كامل.
     */
    private computeScreenToMercator(): Mat3 | null {
        const map = this.map;
        if (!map) return null;
        // من المخزَّن لا من DOM — راجع cssW/cssH.
        const w = this.cssW;
        const h = this.cssH;
        if (w < 2 || h < 2) return null;

        const centerX = (map.getCenter().lng + 180) / 360;

        // قطر النافذة بوحدة «عالم» عند هذا التقريب. نصف القطر هو أطول مسار من المركز
        // إلى ركن، فخطوةٌ منه أقصر من نصف عالم تتطلّب steps > spanWorlds. والسقف 16
        // يحمي من الإمالة الحادّة حيث يبتعد الأفق بلا حدّ.
        const worldPx = 512 * Math.pow(2, map.getZoom());
        const spanWorlds = Math.hypot(w, h) / Math.max(worldPx, 1);
        const steps = Math.max(1, Math.min(16, Math.ceil(spanWorlds) + 1));

        const cx = w * 0.5, cy = h * 0.5;
        const corners: Array<[number, number]> = [];
        for (const [px, py] of [[0, 0], [w, 0], [w, h], [0, h]] as const) {
            let ref = centerX;
            let m: [number, number] | null = null;
            for (let i = 1; i <= steps; i++) {
                const t = i / steps;
                let ll: { lng: number; lat: number } | null = null;
                try {
                    ll = map.unproject([cx + (px - cx) * t, cy + (py - cy) * t]);
                } catch {
                    return null;
                }
                if (!ll || !Number.isFinite(ll.lng) || !Number.isFinite(ll.lat)) return null;
                m = lngLatToMercator(ll.lng, ll.lat);
                m[0] -= Math.round(m[0] - ref);
                ref = m[0];
            }
            if (!m) return null;
            corners.push(m);
        }

        // حارسُ الانهيار: رباعيٌّ ضلعه صفر يعطي مصفوفةً منتهيةً لكنها تُسقط الشاشة كلّها
        // على خطّ. لا نمرّرها للـ GPU — نُبقي مصفوفة الإطار السابق حتى يعود المنظر سليماً.
        const spanX = Math.max(
            Math.abs(corners[1][0] - corners[0][0]),
            Math.abs(corners[2][0] - corners[3][0]),
        );
        const spanY = Math.max(
            Math.abs(corners[3][1] - corners[0][1]),
            Math.abs(corners[2][1] - corners[1][1]),
        );
        if (spanX < 1e-6 || spanY < 1e-6) return null;

        const s2m = squareToQuad(corners[0], corners[1], corners[2], corners[3]);
        return isFinite3(s2m) ? s2m : null;
    }

    /** هل المصفوفتان متطابقتان بتّةً بتّة؟ (الخريطة لم تتحرّك بين الإطارين). */
    private static sameMat(a: Mat3, b: Mat3): boolean {
        for (let i = 0; i < 9; i++) if (a[i] !== b[i]) return false;
        return true;
    }

    /** خطوة السرعة: بكسل CSS لكل (م/ث) لكل إطار — صيغة Zoom Earth الحرفية. */
    private computeSpeedK(): number {
        const zoom = this.map?.getZoom() ?? 3;
        return ZE_PX_PER_MS / Math.max(1, ZE_SLOW_ZOOM - zoom);
    }

    // ── الرسم ─────────────────────────────────────────────────────────────────

    render(gl: GLContext, options: CustomRenderMethodInput) {
        try {
            this.renderInner(gl, options);
        } catch (e) {
            if (!this.errorLogged) {
                console.error(`ParticleGLLayer(${this.id}) render error:`, e);
                this.errorLogged = true;
            }
        }
        this.map?.triggerRepaint(); // حركة مستمرة
    }

    private renderInner(gl: GLContext, _options: CustomRenderMethodInput) {
        if (!this.updateProgram || !this.drawProgram || !this.screenProgram) return;
        if (!this.framebuffer || !this.posTex0 || !this.ageTex0) return;

        const cssW = this.cssW || 1280;
        const cssH = this.cssH || 720;

        // التحويل الحالي، والتحويل من شاشة الإطار السابق إلى شاشة هذا الإطار.
        // ملاحظة: prevS2m لا يُحدَّث إلا بعد محاكاة فعلية — فهو يصف الفضاء الذي كُتبت به
        // نسج الحالة والذيول، وإن تخطّينا إطاراً (لا شبكة رياح) وتحرّكت الخريطة خلاله
        // بقي التحويل صحيحاً بالنسبة لما هو مخزَّن فعلاً.
        const s2m = this.computeScreenToMercator();
        if (!this.gridCur || !s2m) return;
        const prevToCur: Mat3 = this.prevS2m
            ? (ParticleGLLayer.sameMat(s2m, this.prevS2m)
                ? (IDENTITY3.slice() as Mat3)   // الخريطة ساكنة → تحويل الوحدة بالضبط (بلا تشويش تراكمي)
                : multiply3(invert3(s2m), this.prevS2m))
            : (IDENTITY3.slice() as Mat3);
        this.prevS2m = s2m;

        // زمن الإطار بوحدات 60fps، محصور لتفادي قفزة بعد تعليق التبويب.
        const now = typeof performance === 'undefined' ? 0 : performance.now();
        this.frameStep = this.lastFrameMs > 0 && now > 0
            ? Math.max(0.25, Math.min(2.5, (now - this.lastFrameMs) / (1000 / 60)))
            : 1;
        this.lastFrameMs = now;

        // تقدّم الزمن المعروض نحو هدف الشريط الزمني بتدرّج (راجع setModelTime).
        if (this.rampMs > 0) {
            const k = Math.min(1, (now - this.rampStart) / this.rampMs);
            this.shownTime = this.rampFrom + (this.rampTo - this.rampFrom) * k;
            if (k >= 1) this.rampMs = 0;
        }
        // الساعتان المحيطتان بالزمن المعروض ونسبة المزج بينهما (المزج نفسه على الـ GPU).
        let lo = Math.floor(this.shownTime);
        let windBlend = this.shownTime - lo;
        let gLo = this.gridForIndex(lo);
        let gHi = this.gridForIndex(lo + 1);
        if (!gLo) { lo = this.gridIndex; windBlend = 0; gLo = this.gridCur; gHi = this.gridNext; }
        if (!gHi) { gHi = gLo; windBlend = 0; }
        this.ensureWindSlot(gl, 0, lo, gLo);
        this.ensureWindSlot(gl, 1, lo + 1, gHi);

        // العدد يتناسب مع مساحة الشاشة (بوحدات CSS)، مع تعزيز تدريجي عند التصغير:
        // Zoom Earth تُبطئ الحركة تحت zoom 5 (‎÷max(1,5−zoom)‎) فيقصر الخيط وتقلّ التغطية
        // المرئية؛ زيادة العدد تُعيد الإحساس بالكثافة نفسه في العرض الواسع.
        // عدد ZE الحرفي: لا تعزيز بالزووم — الكثافة على الشاشة واحدة في كل المستويات.
        const target = Math.max(ZE_MIN_PARTICLES, Math.min(
            this.numParticles, ZE_MAX_PARTICLES,
            Math.round(cssW * cssH * DENSITY_PER_CSS_PX),
        ));
        // تنعيم بثابت زمني ~0.3ث: أي قفزة في الزووم (نقر مزدوج/flyTo) تُوزَّع على عدّة
        // إطارات، فتظهر الخيوط الجديدة تدريجياً بدل أن تنبثق دفعة واحدة بلا ذيول.
        this.smoothCount = this.smoothCount <= 0
            ? target
            : this.smoothCount + (target - this.smoothCount) * Math.min(1, this.frameStep * 0.06);
        this.numDrawn = Math.max(1, Math.round(this.smoothCount));

        const reseed = this.pendingReseed;
        this.pendingReseed = false;
        if (reseed) this.clearTrails = true;

        // MapLibre قد يترك هذه مفعّلة فتقصّ رسمنا أو تمنع المسح.
        gl.disable(gl.CULL_FACE);
        gl.disable(gl.DEPTH_TEST);
        gl.disable(gl.SCISSOR_TEST);
        gl.disable(gl.STENCIL_TEST);

        this.ensureScreenTextures(gl);
        const prevFbo = gl.getParameter(gl.FRAMEBUFFER_BINDING) as WebGLFramebuffer | null;

        // 1) المحاكاة → posTex1 / ageTex1
        this.updateParticles(gl, prevToCur, s2m, cssW, cssH, reseed, windBlend);

        // 2) نسيج الذيول: إعادة إسقاط الإطار السابق مع التلاشي، ثم رسم قطع هذا الإطار فوقه
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.screenTex1, 0);
        gl.viewport(0, 0, this.screenW, this.screenH);

        // المسح ضروري: عند التصغير لا يغطّي الرباعي المُعاد إسقاطه كامل الشاشة، فتبقى
        // بقايا إطار أقدم في الحلقة (ping-pong) على الحواف بلا هذا المسح.
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);

        const keep = Math.pow(ZE_TRAIL_KEEP, this.frameStep);
        const fade = this.clearTrails ? 0 : keep;
        this.clearTrails = false;

        gl.disable(gl.BLEND);
        if (fade > 0.001) {
            // 0.55/255: الضرب وحده على RGBA8 يعلَق عند القيم الصغيرة فتبقى بقايا ذيول
            // باهتة دائمة؛ الطرح الصغير يضمن التلاشي إلى الصفر.
            this.drawScreenQuad(gl, this.screenTex0!, fade, (0.45 / 255) * this.frameStep, prevToCur);
        }

        gl.enable(gl.BLEND);
        gl.blendEquation(gl.FUNC_ADD);
        // مزج مسبق الضرب بالألفا: يمنع تراكب الجسيمات من الحرق إلى أبيض ناصع.
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        this.drawParticles(gl, prevToCur);

        // 3) الدمج على إطار الخريطة
        gl.bindFramebuffer(gl.FRAMEBUFFER, prevFbo);
        gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
        gl.enable(gl.BLEND);
        gl.blendEquation(gl.FUNC_ADD);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        this.drawScreenQuad(gl, this.screenTex1!, 1.0, 0, IDENTITY3 as Mat3);

        // تبديل (ping-pong)
        [this.posTex0, this.posTex1] = [this.posTex1, this.posTex0];
        [this.ageTex0, this.ageTex1] = [this.ageTex1, this.ageTex0];
        [this.screenTex0, this.screenTex1] = [this.screenTex1, this.screenTex0];

        // تنظيف حالة GL حتى لا نفسد رسم MapLibre التالي
        this.disableOwnAttribs(gl);
        gl.bindBuffer(gl.ARRAY_BUFFER, null);
        for (let unit = 3; unit >= 0; unit--) {
            gl.activeTexture(gl.TEXTURE0 + unit);
            gl.bindTexture(gl.TEXTURE_2D, null);
        }
    }

    /**
     * **حرِج:** يعطّل كل خاصيات الرأس التي فعّلناها. بدونه تبقى مفعّلة على حالة GL
     * المشتركة بعد إزالة الطبقة وقد أُتلفت مخازنها — فيفشل كل رسم لاحق لطبقات WebGL
     * المخصّصة الأخرى بـ INVALID_OPERATION وتختفي من الخريطة.
     */
    private disableOwnAttribs(gl: GLContext) {
        for (const locSet of [this.updateLoc, this.drawLoc, this.screenLoc]) {
            if (!locSet) continue;
            for (const loc of Object.values(locSet.attributes)) {
                if (loc >= 0) gl.disableVertexAttribArray(loc);
            }
        }
    }

    private updateParticles(
        gl: GLContext, prevToCur: Mat3, s2m: Mat3, cssW: number, cssH: number, reseed: boolean,
        windBlend: number,
    ) {
        if (!this.updateLoc || !this.framebuffer || !this.gridCur) return;
        const u = this.updateLoc.uniforms;

        gl.bindFramebuffer(gl.FRAMEBUFFER, this.framebuffer);
        gl.viewport(0, 0, STATE_RES, STATE_RES);
        gl.disable(gl.BLEND);

        gl.useProgram(this.updateProgram);
        this.bindAttrib(gl, this.quadBuffer!, this.updateLoc.attributes.a_pos, 2);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.posTex0);
        gl.uniform1i(u.u_pos, 0);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, this.ageTex0);
        gl.uniform1i(u.u_age, 1);
        gl.activeTexture(gl.TEXTURE2);
        gl.bindTexture(gl.TEXTURE_2D, this.windSlots[0].tex);
        gl.uniform1i(u.u_wind, 2);
        gl.activeTexture(gl.TEXTURE3);
        gl.bindTexture(gl.TEXTURE_2D, this.windSlots[1].tex);
        gl.uniform1i(u.u_wind_next, 3);
        gl.uniform1f(u.u_wind_blend, Math.max(0, Math.min(1, windBlend)));

        gl.uniformMatrix3fv(u.u_prev_to_cur, false, toColumnMajor(prevToCur, this.tmpMat));
        gl.uniformMatrix3fv(u.u_s2m, false, toColumnMajor(s2m, this.tmpMat2));

        const b = this.gridCur.bounds;
        gl.uniform4f(u.u_bounds, b.west, b.south, b.east, b.north);
        gl.uniform1f(u.u_wrap_x, Math.abs(b.east - b.west) >= 359.9 ? 1 : 0);

        const k = this.computeSpeedK();
        gl.uniform2f(u.u_step, k / cssW, k / cssH);
        gl.uniform1f(u.u_frame_step, this.frameStep);
        gl.uniform1f(u.u_age_step, this.frameStep / ZE_MAX_AGE);
        gl.uniform1f(u.u_margin, VIEW_MARGIN);
        gl.uniform1f(u.u_rand_seed, Math.random());
        gl.uniform1f(u.u_reseed, reseed ? 1 : 0);
        gl.uniform1f(u.u_uv_max, UV_MAX_MS);

        // حفظ الكثافة عند التصغير: λ = معامل تغيّر المقياس الخطّي في هذا الإطار.
        // λ < 1 (تصغير) ⇒ نُميت النسبة (1 − λ²) ونعيد بذرها موحّدةً على النافذة الجديدة،
        // فتمتلئ الحلقة المكشوفة حديثاً فوراً بدل أن تظل شبه فارغة دورة حياة كاملة.
        const zoom = this.map?.getZoom() ?? 3;
        const lambda = Math.pow(2, zoom - (this.prevZoom ?? zoom));
        this.prevZoom = zoom;
        gl.uniform1f(u.u_scale_kill, Math.max(0, Math.min(0.9, 1 - lambda * lambda)));

        // تمريرتان بنفس البرنامج: الموضع ثم العمر. كلتاهما تقرآن الحالة القديمة نفسها
        // وتحسبان قرار الموت نفسه، فتبقيان متّسقتين بلا حاجة إلى MRT أو إضافات.
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.posTex1, 0);
        gl.uniform1f(u.u_out_age, 0);
        gl.drawArrays(gl.TRIANGLES, 0, 6);

        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.ageTex1, 0);
        gl.uniform1f(u.u_out_age, 1);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    private drawParticles(gl: GLContext, prevToCur: Mat3) {
        if (!this.drawLoc) return;
        const u = this.drawLoc.uniforms;

        gl.useProgram(this.drawProgram);
        this.bindAttrib(gl, this.indexBuffer!, this.drawLoc.attributes.a_index, 1);
        this.bindAttrib(gl, this.cornerBuffer!, this.drawLoc.attributes.a_corner, 1);

        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, this.posTex1);
        gl.uniform1i(u.u_pos_new, 0);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, this.posTex0);
        gl.uniform1i(u.u_pos_old, 1);
        gl.activeTexture(gl.TEXTURE2);
        gl.bindTexture(gl.TEXTURE_2D, this.ageTex1);
        gl.uniform1i(u.u_age_new, 2);

        gl.uniformMatrix3fv(u.u_prev_to_cur, false, toColumnMajor(prevToCur, this.tmpMat));
        gl.uniform1f(u.u_res, STATE_RES);
        gl.uniform2f(u.u_resolution, this.screenW, this.screenH);
        gl.uniform1f(u.u_margin, VIEW_MARGIN);

        // السمك بوحدة CSS ثابتة عبر الزوم (خيط Zoom Earth/Windy ~1.5 بكسل CSS).
        const dpr = this.screenW / Math.max(1, this.cssW || this.screenW);
        // الافتراضي (lineWidth 0.5 ⇒ 1.5) يعطي متوسّط ZE بالضبط؛ المقبض يبقى نسبياً.
        gl.uniform1f(u.u_width, ZE_WIDTH_MEAN * dpr);
        // نثر السماكة ثابت على مجموعات ZE الأربع (0.56..2.25) — لا يتبع الإعداد، فهو ما يعطي خليط الرفيع والعريض.
        gl.uniform1f(u.u_width_var, ZE_WIDTH_VAR);
        // ZE: 2/3 فوق القاعدة الداكنة و0.8 فوق الفاتحة (vw[layer] || .8) — بدونها تذوب الخيوط في الحرارة.
        gl.uniform1f(u.u_alpha, this.darkBase ? ZE_OPACITY : 0.8);
        gl.uniform1f(u.u_max_seg, 120 * dpr);
        gl.uniform1f(u.u_min_seg, MIN_SEGMENT_CSS_PX * dpr);
        const [r, g, bl] = hexToRgb01(ZE_COLOR);
        gl.uniform3f(u.u_color, r, g, bl);

        gl.drawArrays(gl.TRIANGLES, 0, this.numDrawn * ParticleGLLayer.VERTS_PER_PARTICLE);
    }

    private drawScreenQuad(
        gl: GLContext, texture: WebGLTexture, opacity: number, floor: number, warp: Mat3,
    ) {
        if (!this.screenLoc) return;
        gl.useProgram(this.screenProgram);
        this.bindAttrib(gl, this.quadBuffer!, this.screenLoc.attributes.a_pos, 2);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.uniform1i(this.screenLoc.uniforms.u_screen, 0);
        gl.uniform1f(this.screenLoc.uniforms.u_opacity, opacity);
        gl.uniform1f(this.screenLoc.uniforms.u_floor, floor);
        gl.uniformMatrix3fv(this.screenLoc.uniforms.u_warp, false, toColumnMajor(warp, this.tmpMat));
        gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    private bindAttrib(gl: GLContext, buffer: WebGLBuffer, location: number, size: number) {
        if (location < 0) return;
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.enableVertexAttribArray(location);
        gl.vertexAttribPointer(location, size, gl.FLOAT, false, 0, 0);
    }
}
